// Client HTTP unique de l'application.
//
// Aucun `fetch` ne doit exister ailleurs : c'est ici que se décident la gestion
// de session, le format d'erreur et l'en-tête de contenu. L'ancien frontend
// dispersait ses appels et a payé un bug réel pour ça — il envoyait
// `content-type: application/json` sur un POST SANS corps, Fastify répondait
// 400, et la DÉCONNEXION échouait en silence : la session restait valide alors
// que l'interface affichait l'écran de connexion.

/** Erreur d'API portant le statut HTTP et le code métier du serveur. */
export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  /** Corps brut, pour les routes qui renvoient des données utiles avec l'erreur
   *  (par exemple `readiness` sur un refus 422 NOT_READY). */
  readonly body: unknown;

  constructor(message: string, status: number, code?: string, body?: unknown) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.body = body;
  }

  /** La session a expiré ou n'existe pas : à traiter comme une déconnexion. */
  get isUnauthenticated(): boolean {
    return this.status === 401;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

function messageFor(status: number, payload: unknown): string {
  if (payload && typeof payload === 'object' && 'error' in payload) {
    const value = (payload as { error?: unknown }).error;
    if (typeof value === 'string' && value.trim()) return value;
  }
  if (status === 401) return 'Session expirée — reconnectez-vous.';
  if (status === 403) return "Vous n'avez pas les droits pour cette action.";
  if (status === 404) return 'Introuvable dans votre périmètre.';
  if (status >= 500) return 'Le serveur a rencontré une erreur.';
  return `Échec de la requête (${status}).`;
}

function codeFor(payload: unknown): string | undefined {
  if (payload && typeof payload === 'object' && 'code' in payload) {
    const value = (payload as { code?: unknown }).code;
    if (typeof value === 'string') return value;
  }
  return undefined;
}

export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {};
  // On ne déclare le type de contenu QUE s'il y a un corps : sinon le serveur
  // rejette la requête (corps JSON vide interdit). Voir l'en-tête de ce fichier.
  if (body !== undefined) headers['content-type'] = 'application/json';

  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers,
      // Le cookie de session est httpOnly : il faut l'envoyer explicitement.
      credentials: 'same-origin',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    // Panne réseau : distinguée d'une erreur applicative, car la conduite à
    // tenir n'est pas la même (réessayer, plutôt que corriger sa saisie).
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiRequestError('Serveur injoignable — vérifiez votre connexion.', 0);
  }

  let payload: unknown = null;
  const type = response.headers.get('content-type') ?? '';
  if (type.includes('application/json')) {
    try {
      payload = await response.json();
    } catch {
      payload = null;
    }
  }

  if (!response.ok) {
    throw new ApiRequestError(
      messageFor(response.status, payload),
      response.status,
      codeFor(payload),
      payload,
    );
  }

  return payload as T;
}

export const api = {
  request,
  get: <T>(path: string, signal?: AbortSignal) =>
    request<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'POST' } : { method: 'POST', body }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'PUT' } : { method: 'PUT', body }),
  // Un DELETE peut porter un corps : la suppression d'un projet exige que le
  // nom soit retapé, et cette confirmation n'a rien à faire dans l'URL (elle
  // finirait dans les journaux d'accès du serveur et du navigateur).
  del: <T>(path: string, body?: unknown) =>
    request<T>(path, body === undefined ? { method: 'DELETE' } : { method: 'DELETE', body }),
};

/** Adresse d'envoi d'un fichier, ouverte par le serveur. */
export interface UploadTarget {
  url: string;
  method: string;
  headers: Record<string, string>;
}

/**
 * Envoie un fichier (enregistrement d'entretien, pièce terrain) à l'adresse
 * ouverte par le serveur, avec la progression. En production l'adresse est
 * celle du bucket (session resumable), en local celle de l'API.
 */
export function putFile(upload: UploadTarget, file: Blob, onProgress: (ratio: number) => void): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(upload.method, upload.url);
    // Le cookie de session ne sert que vers notre API (local) ; vers le
    // bucket, l'URL de session EST l'autorisation.
    xhr.withCredentials = upload.url.startsWith('/');
    for (const [k, v] of Object.entries(upload.headers)) xhr.setRequestHeader(k, v);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300
      ? resolve()
      : reject(new Error(`L’envoi a été refusé (${xhr.status}). Réessayez.`)));
    xhr.onerror = () => reject(new Error('L’envoi a été interrompu (réseau). Réessayez.'));
    xhr.send(file);
  });
}
